import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { type NavItem } from '@/lib/layout.config';
import {
  SIDEBAR_NAV_CHILDREN_CLASSES,
  SIDEBAR_NAV_ROW_CLASSES,
  SIDEBAR_NAV_ROW_IDLE_CLASSES,
  SIDEBAR_NAV_ROW_LOCATED_CLASSES,
} from '@/shared/components/Layout/SidebarNav/sidebar-nav.tokens';
import { SidebarNavLink } from '@/shared/components/Layout/SidebarNavLink/SidebarNavLink';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/ui/Collapsible/Collapsible';
import { cn } from '@/shared/utils/cn/cn.util';

import { useSidebarNavGroupLogic } from './useSidebarNavGroupLogic';

export interface SidebarNavGroupProps {
  item: NavItem;
  /** Already permission-filtered and non-empty — `SidebarNavItem` saw to both. */
  items: readonly NavItem[];
  label: string;
}

/**
 * A group's disclosure: the header is a BUTTON that opens and closes it, never a
 * link — a row that both navigates and toggles is the one every operator
 * mis-clicks. The group's front door is still one click away, as its first child.
 *
 * The chevron points down when open and along the reading direction when
 * closed: `-rotate-90` turns it to the right, `rtl:rotate-90` to the left.
 */
export function SidebarNavGroup({ item, items, label }: SidebarNavGroupProps) {
  const { t } = useTranslation('common');
  const { isOpen, activeChildTo, toggle } = useSidebarNavGroupLogic(item, items);
  const Icon = item.icon;
  const isLocated = activeChildTo !== undefined && !isOpen;

  return (
    <Collapsible open={isOpen} onOpenChange={toggle}>
      <CollapsibleTrigger
        data-active={isLocated ? 'true' : undefined}
        className={cn(
          SIDEBAR_NAV_ROW_CLASSES,
          isLocated ? SIDEBAR_NAV_ROW_LOCATED_CLASSES : SIDEBAR_NAV_ROW_IDLE_CLASSES,
        )}
      >
        <Icon aria-hidden className="h-4 w-4 shrink-0" />
        <span className="flex-1 truncate text-start">{label}</span>
        <ChevronDown
          aria-hidden
          className={cn(
            'h-4 w-4 shrink-0 transition-transform duration-fast ease-standard',
            isOpen ? 'rotate-0' : '-rotate-90 rtl:rotate-90',
          )}
        />
      </CollapsibleTrigger>

      <CollapsibleContent className="data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-1">
        <ul aria-label={label} className={SIDEBAR_NAV_CHILDREN_CLASSES}>
          {items.map((child) => (
            <li key={child.to}>
              <SidebarNavLink
                item={child}
                label={t(child.labelKey)}
                isActive={child.to === activeChildTo}
                isCollapsed={false}
              />
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}
